/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/media-has-caption */
import React, { useState, useEffect, useRef, useCallback, memo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import WaveForm from '../../MediaContainer/WaveForm';
import VideoPlayerOverlay from '../../VideoPlayerOverlay';

import classes from './styles.module.scss';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import { useMeetingViewStore } from '../../../store';

/* function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
} */

function MeetingVideoPlayer({
  currentAudioSectionWithoutBackground,
  currentAudioSectionProgress,
  meeting,
  waveFormContainerRef,
  videoRef,
  isDraggingProgressBar,
  progressBarRef,
  videoDuration,
  animateProgressBar,
  stopAnimation,
  isPlaying,
  setIsPlaying,
  meetingVersion,
  confirmMeetingView,
  folderId,
  title,
  signedS3Url,
  fullscreenContainerRef,
  isMobileUpload,
  onRewind,
  subtitles,
  miniVideoRef,
}) {
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isViewConfirmed, setIsViewConfirmed] = useState(false);

  const trackRef = useRef();

  const navigate = useNavigate();
  const location = useLocation();

  const isRequestFullscreenSupported =
    !!fullscreenContainerRef.current?.requestFullscreen;

  const isMobile = useCheckMobileScreen();

  const isVideoPlayerFixedMode = useMeetingViewStore(
    (state) => state.isVideoPlayerFixedMode
  );
  const setIsVideoPlayerFixedMode = useMeetingViewStore(
    (state) => state.setIsVideoPlayerFixedMode
  );

  const handleBackClick = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);
    const isFromEmail = queryParams.get('fromEmail');

    if (isFromEmail && folderId) {
      navigate(`/my-library/${folderId}`);
    } else if (isFromEmail) {
      navigate('/my-videos');
    } else {
      navigate(-1);
    }
  }, [location.search, navigate, folderId]);

  useEffect(() => {
    const handleOrientationChange = async () => {
      const isLandscape = window.screen.orientation.type.includes('landscape');
      try {
        if (isVideoPlayerFixedMode && !isLandscape) {
          setIsVideoPlayerFixedMode(false);
        }

        if (isRequestFullscreenSupported) {
          if (isLandscape) {
            await fullscreenContainerRef.current.requestFullscreen();
          } else if (!isLandscape && document.fullscreenElement) {
            document.exitFullscreen();
          }
        } else if (isLandscape) {
          setIsVideoPlayerFixedMode(false);
        }
      } catch (error) {
        setIsVideoPlayerFixedMode(true);
        console.error('Error requesting fullscreen:', error);
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [
    fullscreenContainerRef,
    isRequestFullscreenSupported,
    isVideoPlayerFixedMode,
    setIsVideoPlayerFixedMode,
  ]);

  // Confirm meeting view as component mounts
  useEffect(() => {
    if (!isViewConfirmed) {
      confirmMeetingView();
    }
  }, [confirmMeetingView, isViewConfirmed]);

  // Set video ready state
  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const setReadyState = () => {
      setIsVideoReady(true);
    };

    // videoRef.current.addEventListener('canplay', setReadyState);
    videoRef.current.addEventListener('loadedmetadata', setReadyState);

    return () => {
      if (videoRef.current) {
        // videoRef.current.removeEventListener('canplay', setReadyState);

        videoRef.current.removeEventListener('loadedmetadata', setReadyState);
      }
    };
  }, [videoRef]);

  const playButtonHandler = () => {
    setIsPlaying(true);
    videoRef.current.play();
    if (miniVideoRef?.current) {
      miniVideoRef.current.currentTime = videoRef.current.currentTime;
      miniVideoRef.current.play();
    }
    animateProgressBar();

    setIsViewConfirmed(true);
  };

  const pauseButtonHandler = () => {
    setIsPlaying(false);
    videoRef.current.pause();
    if (miniVideoRef?.current) {
      miniVideoRef.current.pause();
      miniVideoRef.current.currentTime = videoRef.current.currentTime;
    }
    stopAnimation();
  };

  return (
    <div
      className={classNames(classes.MeetingVideoPlayer, {
        [classes.mobileUpload]: isMobileUpload,
        [classes.fixed]: isVideoPlayerFixedMode,
      })}
      ref={fullscreenContainerRef}
    >
      {(isRequestFullscreenSupported || !isMobile) && (
        <div className={classes.controlsOverlay}>
          <VideoPlayerOverlay
            meetingTitle={meeting ? meeting.title : title}
            videoRef={videoRef}
            viewOnly
            videoDuration={videoDuration}
            progressBarRef={progressBarRef}
            isVideoReady={isVideoReady}
            videoSectionPlayerRef={videoRef}
            stopVideoPlayer={pauseButtonHandler}
            pauseVideoPlayer={pauseButtonHandler}
            startVideoPlayer={playButtonHandler}
            isPlaying={isPlaying}
            arePlayerButtonsVisible={isVideoReady}
            isDraggingProgressBar={isDraggingProgressBar}
            onBackClick={handleBackClick}
            fullscreenContainerRef={fullscreenContainerRef}
            onRewind={onRewind}
            hasSubtitles={!!subtitles}
            trackRef={trackRef}
            secondaryVideoRef={miniVideoRef}
          />
        </div>
      )}
      {currentAudioSectionWithoutBackground && !meetingVersion && (
        <div className={classes.waveFormContainer} ref={waveFormContainerRef}>
          <WaveForm
            source={currentAudioSectionWithoutBackground.section.signedS3Url}
            containerRef={fullscreenContainerRef}
            fullHeight
            seek={currentAudioSectionProgress}
          />
        </div>
      )}
      <video
        playsInline
        src={`${meeting ? meeting.signedS3Url : signedS3Url}#t=0.001`}
        ref={videoRef}
        style={{ visibility: currentAudioSectionWithoutBackground && 'hidden' }}
        controls={!isRequestFullscreenSupported && isMobile}
        crossOrigin="anonymous"
      >
        {subtitles && (
          <track
            src={subtitles}
            kind="metadata"
            srcLang="en"
            label="English"
            default
            ref={trackRef}
            style={{ display: 'none' }}
          />
        )}
      </video>
      <div
        className={classes.controlsContainer}
        style={{
          visibility: isVideoReady ? 'visible' : 'hidden',
        }}
      />
    </div>
  );
}

export default memo(MeetingVideoPlayer);
